// The OFC (BC Office of the Fire Commissioner) Minimum Standards and the
// technical competency catalogs every training record is measured against.
//
// Moved here from embertracking/src/data/standards.js (#2204) so the api can
// reason about the same catalog the browser renders — Ask answers "which JPRs
// are we missing", and that has to agree with the Training Matrix to the
// letter. The department app still imports these through -/data/standards,
// which re-exports them; the NFPA and WorksafeBC reference TEXT stays there,
// because it is large and only the browser displays it.
//
// Pure data and pure helpers: no lodash, no React, nothing from this package's
// CJS (see common/test/browserSafeEsm.test.js).

export const Competencies = {
    generalKnowledge: {
        level: 'exterior',
        label: 'General Knowledge, Safety, and Communications',
        shortLabel: 'General Knowledge'
    },
    ppeSCBA: {
        level: 'exterior',
        label: 'Personal Protective Clothing & Equipment (SCBA)',
        shortLabel: 'PPE & SCBA',
        otherRequirements: ['WorkSafeBC OHS Part 31 (31.10 to 31.26 & 31.38)']
    },
    waterSupply: {
        level: 'exterior',
        label: 'Water Supply'
    },
    hoseLinesNozzles: {
        level: 'exterior',
        label: 'Hose Lines, Nozzles, Appliances, and Fire Streams',
        shortLabel: 'Hose Lines & Nozzles'
    },
    groundLadders: {
        level: 'exterior',
        label: 'Ground Ladders',
        otherRequirements: ['WorkSafeBC OHS Part 31 (31.37)']
    },
    sceneLighting: {
        level: 'exterior',
        label: 'Scene Lighting and Utilities',
        shortLabel: 'Scene Lighting'
    },
    ropesKnots: {
        level: 'exterior',
        label: 'Ropes and Knots – Hoisting Tools and Equipment',
        otherRequirements: ['WorkSafeBC OHS Part 31 (31.39)'],
        shortLabel: 'Ropes & Knots'
    },
    buildingConstruction: {
        level: 'exterior',
        label: 'Building Construction and Fire Behaviour',
        shortLabel: 'Building Construction'
    },
    gasElectricity: {
        level: 'exterior',
        label: 'Gas & Electrical Safety for Firefighters',
        shortLabel: 'Gas & Electricity'
    },
    safeWorkAreas: {
        level: 'exterior',
        label: 'Establish Safe Work Areas, Traffic, and Scene Control',
        otherRequirements: ['WorkSafeBC OHS Part 31 (31.1(d), 18)'],
        shortLabel: 'Safe Work Areas'
    },
    forcibleEntry: {
        level: 'exterior',
        label: 'Forcible Entry'
    },
    ventilation: {
        level: 'exterior',
        label: 'Ventilation – Horizontal',
        shortLabel: 'Ventilation'
    },
    fireAttackExtinguishment: {
        level: 'exterior',
        label: 'Exterior Fire Attack/Extinguishment',
        shortLabel: 'Fire Attack'
    },
    hazmatAwareness: {
        level: 'exterior',
        label: 'HAZMAT Awareness'
    },
    ics100: {
        level: 'exterior',
        label: 'ICS 100'
    },

    // Driver/Operator
    evo: {
        level: 'driverOperator',
        label: 'Emergency Vehicle Operations (EVO)',
        shortLabel: 'EVO'
    },

    // Interior
    communications: {
        level: 'interior',
        label: 'Communications'
    },
    exitHazardousArea: {
        level: 'interior',
        label: 'Exit a Hazardous Area'
    },
    searchRescue: {
        level: 'interior',
        label: 'Conduct a Search and Rescue',
        shortLabel: 'Search & Rescue'
    },
    overhaulSalvage: {
        level: 'interior',
        label: 'Loss Control – Overhaul and Salvage',
        shortLabel: 'Overhaul & Salvage'
    },
    preIncidentPlanning: {
        level: 'interior',
        label: 'Pre-Incident Planning'
    },
    interiorFireAttack: {
        level: 'interior',
        label: 'Interior Fire Attack/Extinguishment',
        shortLabel: 'Interior Fire Attack'
    },
    rapidInterventionTeam: {
        level: 'interior',
        label: 'Rapid Intervention Team Member (RIT)',
        shortLabel: 'RIT'
    },

    driverOperator: {
        level: 'driverOperator',
        label: 'Driver Operator'
    },
    incidentCommander: {
        level: 'interior',
        label: 'Incident Commander',
        shortLabel: 'IC'
    },

    incidentManagement: {
        level: 'interior',
        label: 'Incident Management'
    },
    personnelManagement: {
        level: 'interior',
        label: 'Personnel Management and Supervision',
        shortLabel: 'Personnel Management'
    },
    sizeUp: {
        level: 'interior',
        label: 'Task Size-Up'
    },
    incidentSizeUp: {
        level: 'incidentCommander',
        label: 'Incident Size-Up'
    },
    actionPlanning: {
        level: 'interior',
        label: 'Action Planning'
    },
    personnelAccountability: {
        level: 'interior',
        label: 'Personnel Accountability'
    },
    ics200: {
        level: 'interior',
        label: 'ICS 200'
    },
    emergencyServiceDelivery: {
        level: 'interior',
        label: 'Emergency Service Delivery'
    },
    initiateIcs: {
        level: 'interior',
        label: 'Initiate the Incident Command System',
        shortLabel: 'Initiate ICS'
    },
    iso: {
        level: 'interior',
        label: 'Incident Safety Officer',
        shortLabel: 'ISO'
    },
    ics: {
        level: 'interior',
        label: 'Incident Command System',
        shortLabel: 'ICS'
    },
    riskManagement: {
        level: 'interior',
        label: 'Risk Management'
    },
    implementActionPlan: {
        level: 'interior',
        label: 'Implementation of the Tactical Action Plan',
        shortLabel: 'Action Plan Implementation'
    },
    devlopIap: {
        level: 'interior',
        label: 'Develop an Incident Action Plan',
        shortLabel: 'Develop IAP'
    },

    // Full-Service Operations Firefighter (NFPA 1001 Fire Fighter II additions)
    incidentReporting: {
        level: 'fullService',
        label: 'Fire Department Communications – Incident Reporting',
        shortLabel: 'Incident Reporting'
    },
    coordinateFireAttack: {
        level: 'fullService',
        label: 'Coordinate an Interior Attack Line',
        shortLabel: 'Coordinate Attack'
    },
    fireCauseDetermination: {
        level: 'fullService',
        label: 'Protect Evidence of Fire Cause and Origin',
        shortLabel: 'Fire Cause & Evidence'
    },
    vehicleExtrication: {
        level: 'fullService',
        label: 'Vehicle Extrication'
    },
    assistRescueTeams: {
        level: 'fullService',
        label: 'Assist Rescue Operation Teams',
        shortLabel: 'Assist Rescue'
    },
    fireSafetySurvey: {
        level: 'fullService',
        label: 'Fire Safety Survey'
    },
    publicFireSafetyEducation: {
        level: 'fullService',
        label: 'Fire Safety Presentations',
        shortLabel: 'Public Education'
    },
    equipmentMaintenance: {
        level: 'fullService',
        label: 'Maintain Power Plants, Tools, and Lighting',
        shortLabel: 'Equipment Maintenance'
    },
    hoseServiceTesting: {
        level: 'fullService',
        label: 'Annual Service Test on Fire Hose',
        shortLabel: 'Hose Service Test'
    },
    hazmatOperations: {
        level: 'fullService',
        label: 'HAZMAT Operations',
        shortLabel: 'HAZMAT Ops'
    },

    // Company Fire Officer (NFPA 1021 Fire Officer I)
    companyFireOfficer: {
        level: 'fullService',
        label: 'Company Fire Officer',
        shortLabel: 'CFO'
    },
    humanResources: {
        level: 'fullService',
        label: 'Human Resource Management',
        shortLabel: 'Human Resources'
    },
    communityRelations: {
        level: 'fullService',
        label: 'Community and Government Relations',
        shortLabel: 'Community Relations'
    },
    administration: {
        level: 'fullService',
        label: 'Administration'
    },
    inspectionInvestigation: {
        level: 'fullService',
        label: 'Inspection and Investigation',
        shortLabel: 'Inspection & Investigation'
    },
    healthSafety: {
        level: 'fullService',
        label: 'Health and Safety'
    },
    instructorI: {
        level: 'fullService',
        label: 'Fire Service Instructor I',
        shortLabel: 'Instructor I'
    },

    // Training Officer / Instructors (NFPA 1041 Instructor I delivery & evaluation)
    trainingOfficer: {
        level: 'fullService',
        label: 'Training Officer',
        shortLabel: 'TO'
    },
    trainingInstructor: {
        level: 'fullService',
        label: 'Fire Service Instructor I – Delivery and Evaluation',
        shortLabel: 'Instruction & Evaluation'
    }
};

export const OFCMinimumStandards = {
    exterior: {
        generalKnowledge: {
            jprs: [
                '1001:2019:4.1.1',
                '1001:2019:4.1.2',
                '1001:2019:4.2.1',
                '1001:2019:4.2.2',
                '1001:2019:4.2.3',
                '1001:2019:4.2.4',
                '1001:2019:4.3.2',
                '1500:2018:8.1',
                '1500:2018:8.2.1',
                '1500:2018:8.2.5.2',
                '1500:2018:8.5'
            ]
        },
        ppeSCBA: {
            jprs: [
                '1001:2019:4.1.2',
                '1001:2019:4.3.1',
                '1001:2019:4.3.2',
                '1001:2019:4.5.1',
                'WorksafeBC:31.10',
                'WorksafeBC:31.11',
                'WorksafeBC:31.12',
                'WorksafeBC:31.13',
                'WorksafeBC:31.14',
                'WorksafeBC:31.15',
                'WorksafeBC:31.16',
                'WorksafeBC:31.17',
                'WorksafeBC:31.18',
                'WorksafeBC:31.19',
                'WorksafeBC:31.20',
                'WorksafeBC:31.21',
                'WorksafeBC:31.22',
                'WorksafeBC:31.23',
                'WorksafeBC:31.24',
                'WorksafeBC:31.25',
                'WorksafeBC:31.26',
                'WorksafeBC:31.38'
            ]
        },
        waterSupply: {
            jprs: [
                '1001:2019:4.3.15',
                '1001:2019:4.5.1',
                '1001:2019:4.5.2'
            ]
        },
        hoseLinesNozzles: {
            jprs: [
                '1001:2019:4.3.7',
                '1001:2019:4.3.8',
                '1001:2019:4.5.1',
                '1001:2019:4.5.2'
            ]
        },
        groundLadders: {
            jprs: [
                '1001:2019:4.3.6',
                '1001:2019:4.5.1',
                'WorksafeBC:31.37'
            ]
        },
        sceneLighting: {
            jprs: [
                '1001:2019:4.3.17',
                '1001:2019:4.3.18'
            ]
        },
        ropesKnots: {
            jprs: [
                '1001:2019:4.1.2',
                '1001:2019:4.3.20',
                '1001:2019:4.5.1',
                'WorksafeBC:31.39'
            ]
        },
        buildingConstruction: {
            jprs: [
                '1001:2019:4.3.11',
                '220:2018:4',
                '921:2017:5',
                '5000:2018:7'
            ]
        },
        gasElectricity: {
            // TODO outside BC utility
            jprs: [
                'WorksafeBC:31.5'
            ]
        },
        safeWorkAreas: {
            jprs: [
                '1001:2019:4.3.3'
            ]
        },
        forcibleEntry: {
            jprs: [
                '1001:2019:4.3.4'
            ]
        },
        ventilation: {
            jprs: [
                '1001:2019:4.3.11',
                '1001:2019:4.5.1'
            ]
        },
        fireAttackExtinguishment: {
            jprs: [
                '1001:2019:4.3.5',
                '1001:2019:4.3.7',
                '1001:2019:4.3.8',
                '1001:2019:4.3.16',
                '1001:2019:4.3.19',
                '1001:2019:5.3.1',
                '1001:2019:5.3.3'
            ]
        },
        hazmatAwareness: {
            jprs: [
                '1072:2017:3', // OFC says 2019, but it is actually 2017
                '1072:2017:4', // OFC says 2019, but it is actually 2017
                'WorksafeBC:31.5'
            ]
        },
        // ics100: {

        // },

        levels: {
            driverOperator: {
                evo: {
                    jprs: [
                        'WorksafeBC:31.5',
                        'WorksafeBC:31.27',
                        'WorksafeBC:31.28',
                        'WorksafeBC:31.29',
                        'WorksafeBC:31.30',
                        'WorksafeBC:31.31',
                        'WorksafeBC:31.32',
                        '1002:2017:4.2',
                        '1002:2017:4.3',

                        // EVO
                        '1002:2017:5.1',
                        '1002:2017:5.2',

                        // Aerial device
                        '1002:2017:6.1',
                        '1002:2017:6.2'
                    ]
                },
                waterSupply: {
                    jprs: [
                        '1002:2017:4.2',
                        '1002:2017:10.1',
                        '1002:2017:10.2'
                    ]
                }
            },
            teamLeader: {
                incidentManagement: {
                    jprs: [
                        '1001:2019:5.1.1',
                        '1001:2019:5.1.2',
                        '1001:2019:5.2.2',
                        '1001:2019:5.3.4'
                    ]
                },
                personnelManagement: {
                    jprs: [
                        '1021:2020:4.1.1'
                    ]
                },
                sizeUp: {
                    jprs: [
                        '1021:2020:4.2.1',
                        '1021:2020:4.2.2',
                        '1021:2020:4.2.3'
                    ]
                },
                actionPlanning: {
                    jprs: [
                        '1021:2020:4.6.1',
                        '1021:2020:4.6.2'
                    ]
                },
                personnelAccountability: {
                    jprs: [
                        '1500:2018:8.5.2',
                        '1500:2018:8.5.3',
                        '1500:2018:8.5.5',
                        '1500:2018:8.5.6',
                        '1500:2018:8.5.7',
                        '1500:2018:8.5.8',
                        '1500:2018:8.5.9',
                        '1500:2018:8.6.4',
                        '1500:2018:8.6.5',
                        '1500:2018:8.6.6',
                        'WorksafeBC:31.5'
                    ]
                }
                // ics100: {

                // }
            },
            incidentCommander: {
                personnelManagement: {
                    jprs: [
                        '1021:2020:4.1.1'
                    ]
                },
                sizeUp: {
                    jprs: [
                        '1021:2020:4.5.2',
                        '1021:2020:4.5.3'
                    ]
                },
                actionPlanning: {
                    jprs: [
                        '1021:2020:4.6.1',
                        '1021:2020:4.6.2'
                    ]
                },
                emergencyServiceDelivery: {
                    jprs: [
                        '1021:2020:4.1.2'
                    ]
                },
                initiateIcs: {
                    jprs: [
                        '1500:2018:8.1.5',
                        '1500:2018:8.1.6',
                        '1500:2018:8.1.7',
                        '1500:2018:8.1.8'
                    ]
                },
                incidentSizeUp: {
                    jprs: [
                        '1500:2018:8.6.8.1'
                    ]
                },
                personnelAccountability: {
                    jprs: [
                        '1500:2018:8.3',
                        '1500:2018:8.4',
                        '1500:2018:8.5.4',
                        '1500:2018:8.5.12',
                        '1500:2018:8.6',
                        '1500:2018:8.7',
                        '1500:2018:8.9',
                        'WorksafeBC:31.5'
                    ]
                },
                iso: {
                    jprs: [
                        '1521:2020:5.2.1',
                        '1521:2020:5.2.2',
                        '1521:2020:5.2.3',
                        '1521:2020:5.2.4',
                        '1521:2020:5.2.5',
                        '1521:2020:5.2.6',
                        '1521:2020:5.2.7',
                        '1521:2020:5.2.8',
                        '1521:2020:5.2.9',
                        '1521:2020:5.2.10',
                        '1521:2020:5.2.11',
                        '1521:2020:5.2.12',
                        '1521:2020:5.2.13',
                        '1521:2020:5.2.14',
                        '1521:2020:5.2.15',
                        'WorksafeBC:31.6'
                    ]
                }
                // ics200: {
                    
                // }
            }
        }
    },
    
    interior: {
        communications: {
            jprs: [
                '1001:2019:5.2.2'
            ]
        },
        exitHazardousArea: {
            jprs: [
                '1001:2019:4.3.5'
            ]
        },
        searchRescue: {
            jprs: [
                '1001:2019:4.3.9'
            ]
        },
        overhaulSalvage: {
            jprs: [
                '1001:2019:4.3.13',
                '1001:2019:4.3.14'
            ]
        },
        preIncidentPlanning: {
            jprs: [
                '1001:2019:5.5.3',
                '1620:2020:4'
            ]
        },
        interiorFireAttack: {
            jprs: [
                '1001:2019:4.3.9',
                '1001:2019:4.3.10',
                '1001:2019:4.3.13',
                '1001:2019:5.3.1',
                '1001:2019:5.3.3'
            ]
        },
        rapidInterventionTeam: {
            jprs: [
                '1407:2015:4',
                '1407:2015:5',
                '1407:2015:7',
                '1407:2015:8',
                'WorksafeBC:31.23'
            ]
        },
        levels: {
            driverOperator: {
                evo: {
                    jprs: [
                        'WorksafeBC:31.5',
                        'WorksafeBC:31.27',
                        'WorksafeBC:31.28',
                        'WorksafeBC:31.29',
                        'WorksafeBC:31.30',
                        'WorksafeBC:31.31',
                        'WorksafeBC:31.32',
                        '1002:2017:4.2',
                        '1002:2017:4.3',

                        // EVO
                        '1002:2017:5.1',
                        '1002:2017:5.2',

                        // Aerial device
                        '1002:2017:6.1',
                        '1002:2017:6.2'
                    ]
                },
                waterSupply: {
                    jprs: [
                        '1002:2017:4.2',
                        '1002:2017:10.1',
                        '1002:2017:10.2'
                    ]
                }
            },
            teamLeader: {
                incidentManagement: {
                    jprs: [
                        '1001:2019:5.1.1',
                        '1001:2019:5.1.2',
                        '1001:2019:5.2.1'
                    ]
                },
                personnelManagement: {
                    jprs: [
                        '1021:2020:4.1.1',
                        '1021:2020:4.2.1',
                        '1021:2020:4.2.2',
                        '1021:2020:4.2.3'
                    ]
                },
                sizeUp: {
                    jprs: [
                        '1021:2020:4.5.2',
                        '1021:2020:4.5.3'
                    ]
                },
                riskManagement: {
                    jprs: [
                        '1500:2018:4.2',
                        '1500:2018:8.4',
                        '1500:2018:8.5'
                    ]
                },
                actionPlanning: {
                    jprs: [
                        '1001:2019:5.3.2',
                        '1021:2020:4.6.1',
                        '1021:2020:4.6.2'
                    ]
                },
                implementActionPlan: {
                    jprs: [
                        '1001:2019:5.3.4',
                        '1500:2018:8.6.4',
                        '1500:2018:8.6.5',
                        '1500:2018:8.6.6'
                    ]
                },
                communications: {
                    jprs: [
                        '1001:2019:5.3.2'
                    ]
                },
                rapidInterventionTeam: {
                    jprs: [
                        '1500:2018:8.8'
                    ]
                },
                personnelAccountability: {
                    jprs: [
                        '1500:2018:8.5.2',
                        '1500:2018:8.5.3',
                        '1500:2018:8.5.4',
                        '1500:2018:8.5.5',
                        '1500:2018:8.5.6',
                        '1500:2018:8.5.7',
                        '1500:2018:8.5.8',
                        '1500:2018:8.5.9',
                        '1500:2018:8.5.10',
                        '1500:2018:8.5.11',
                        'WorksafeBC:31.5'
                    ]
                },
                iso: {
                    jprs: [
                        '1521:2020:5.2'
                    ]
                }
                // ics200: {

                // }
            },
            ics: {
                ics: {
                    jprs: [
                        '1521:2020:5.3.1',
                        '1521:2020:5.3.2',
                        '1521:2020:5.3.3',
                        '1521:2020:5.3.4'
                    ]
                }
            },
            incidentCommander: {
                riskManagement: {
                    jprs: [
                        '1500:2018:8.6.8.1',
                        '1500:2018:8.7.1',
                        '1500:2018:8.7.4.4.1',
                        '1500:2018:8.7.4.4.4',
                        '1500:2018:8.7.5',
                        '1500:2018:8.8.1',
                        '1500:2018:8.8.4.1',
                        '1500:2018:8.8.7'
                    ]
                },
                initiateIcs: {
                    jprs: [
                        '1500:2018:8.1.1',
                        '1500:2018:8.1.2',
                        '1500:2018:8.1.3',
                        '1500:2018:8.1.5',
                        '1500:2018:8.1.6',
                        '1500:2018:8.1.7',
                        '1500:2018:8.1.8'
                    ]
                },
                devlopIap: {
                    jprs: [
                        '1021:2020:4.6.1'
                    ]
                },
                implementActionPlan: {
                    jprs: [
                        '1021:2020:4.6.2',
                        '1500:2018:8.6.1.3',
                        '1500:2018:8.6.13',
                        '1500:2018:8.6.13.1',
                        '1500:2018:8.6.13.2'
                    ]
                },
                communications: {
                    jprs: [
                        '1500:2018:8.6.15.6',
                        '1500:2018:8.7.2',
                        '1500:2018:8.7.3'
                    ]
                },
                personnelAccountability: {
                    jprs: [
                        'WorksafeBC:31.5'
                    ]
                }
            }
        }
    },

    fullService: {
        // Full-Service Operations Firefighter — NFPA 1001 Fire Fighter II (ch.5)
        // competencies beyond the Interior level.
        incidentReporting: {
            jprs: [
                '1001:2019:5.2.1'
            ]
        },
        coordinateFireAttack: {
            jprs: [
                '1001:2019:5.3.2'
            ]
        },
        fireCauseDetermination: {
            jprs: [
                '1001:2019:5.3.4'
            ]
        },
        vehicleExtrication: {
            jprs: [
                '1001:2019:5.4.1'
            ]
        },
        assistRescueTeams: {
            jprs: [
                '1001:2019:5.4.2'
            ]
        },
        fireSafetySurvey: {
            jprs: [
                '1001:2019:5.5.1'
            ]
        },
        publicFireSafetyEducation: {
            jprs: [
                '1001:2019:5.5.2'
            ]
        },
        equipmentMaintenance: {
            jprs: [
                '1001:2019:5.5.4'
            ]
        },
        hoseServiceTesting: {
            jprs: [
                '1001:2019:5.5.5'
            ]
        },
        hazmatOperations: {
            jprs: [
                '1072:2017:6.2',
                '1072:2017:6.6'
            ]
        },

        levels: {
            companyFireOfficer: {
                humanResources: {
                    jprs: [
                        '1021:2020:4.2'
                    ]
                },
                communityRelations: {
                    jprs: [
                        '1021:2020:4.3'
                    ]
                },
                administration: {
                    jprs: [
                        '1021:2020:4.4'
                    ]
                },
                inspectionInvestigation: {
                    jprs: [
                        '1021:2020:4.5'
                    ]
                },
                emergencyServiceDelivery: {
                    jprs: [
                        '1021:2020:4.6'
                    ]
                },
                healthSafety: {
                    jprs: [
                        '1021:2020:4.7'
                    ]
                },
                instructorI: {
                    jprs: [
                        '1041:2019:4'
                    ]
                }
            },
            trainingOfficer: {
                trainingInstructor: {
                    jprs: [
                        '1041:2019:4.2.1',
                        '1041:2019:4.2.2',
                        '1041:2019:4.2.3',
                        '1041:2019:4.2.4',
                        '1041:2019:4.2.5',
                        '1041:2019:4.3.2',
                        '1041:2019:4.3.3',
                        '1041:2019:4.4.2',
                        '1041:2019:4.4.3',
                        '1041:2019:4.4.4',
                        '1041:2019:4.4.5',
                        '1041:2019:4.5.2',
                        '1041:2019:4.5.3',
                        '1041:2019:4.5.4',
                        '1041:2019:4.5.5',
                        'WorksafeBC:31.4'
                    ]
                }
            }
        }
    }
};

// Competency Categories - defines all available categories and their level structures
export const CompetencyCategories = {
    ofc: {
        label: 'OFC Minimum Standards',
        shortLabel: 'OFC',
        levels: ['exterior', 'interior', 'fullService', 'driverOperator', 'teamLeader', 'incidentCommander']
    },
    ropeRescue: {
        label: 'Rope Rescue',
        shortLabel: 'Ropes',
        levels: ['awareness', 'operations', 'technician']
    },
    autoExtrication: {
        label: 'Auto Extrication',
        shortLabel: 'Auto X',
        levels: ['awareness', 'operations', 'technician']
    },
    firstResponse: {
        label: 'First Response',
        shortLabel: 'First Response',
        levels: null // Flat list, no tiered levels
    },
    wildland: {
        label: 'Wildland / Interface',
        shortLabel: 'Wildland',
        levels: null // Flat list, no tiered levels
    }
};

// Technical Competencies - predefined competencies for each non-OFC category
export const TechnicalCompetencies = {
    ropeRescue: {
        // Awareness (NFPA 1006 Chapter 5.1)
        ropeAwareness: {level: 'awareness', label: 'Rope Rescue Awareness'},
        ropeHazardRecognition: {level: 'awareness', label: 'Hazard Recognition'},
        ropeAssistHaulLine: {level: 'awareness', label: 'Assist Haul Line'},
        // Operations (NFPA 1006 Chapter 5.2)
        ropeKnotsBends: {level: 'operations', label: 'Knots, Bends & Hitches'},
        ropeSingleAnchor: {level: 'operations', label: 'Single Point Anchor'},
        ropeMultiAnchor: {level: 'operations', label: 'Multiple Point Anchor'},
        ropeEdgeProtection: {level: 'operations', label: 'Edge Protection'},
        ropeBelayConstruct: {level: 'operations', label: 'Belay Construction'},
        ropeBelayOperate: {level: 'operations', label: 'Belay Operation'},
        ropeFixedLine: {level: 'operations', label: 'Fixed Rope System'},
        ropeLoweringSystem: {level: 'operations', label: 'Lowering System'},
        ropeRaisingSystem: {level: 'operations', label: 'Raising System (MA)'},
        // Technician (NFPA 1006 Chapter 5.3)
        ropeHighAngle: {level: 'technician', label: 'High Angle Rescue'},
        ropePickoff: {level: 'technician', label: 'Pickoff Rescue'},
        ropeLitterRigging: {level: 'technician', label: 'Litter Rigging'},
        ropeAnchorEvaluation: {level: 'technician', label: 'Anchor Evaluation'},
        ropeChangeOver: {level: 'technician', label: 'System Changeover'}
    },
    autoExtrication: {
        // Awareness (NFPA 1006 Chapter 8.1)
        vehicleAwareness: {level: 'awareness', label: 'Vehicle Rescue Awareness'},
        vehicleSafetyZones: {level: 'awareness', label: 'Safety Zones'},
        vehicleHazardControl: {level: 'awareness', label: 'Hazard Control'},
        // Operations (NFPA 1006 Chapter 8.2)
        vehicleFireProtection: {level: 'operations', label: 'Fire Protection'},
        vehicleStabilization: {level: 'operations', label: 'Vehicle Stabilization'},
        vehicleGlassManagement: {level: 'operations', label: 'Glass Management'},
        vehicleHydraulicTools: {level: 'operations', label: 'Hydraulic Tools'},
        vehicleDisentanglement: {level: 'operations', label: 'Disentanglement'},
        vehicleHybridEV: {level: 'operations', label: 'Hybrid/EV Safety'},
        // Technician (NFPA 1006 Chapter 8.3)
        vehicleHeavyRescue: {level: 'technician', label: 'Heavy Vehicle Rescue'},
        vehicleComplexAccess: {level: 'technician', label: 'Complex Access'},
        vehicleIAP: {level: 'technician', label: 'Incident Action Plan'},
        vehicleBusRescue: {level: 'technician', label: 'Bus/Large Vehicle'}
    },
    firstResponse: {
        // Flat list - BC First Responder (BCEHS) skills
        frSceneSafety: {label: 'Scene Assessment & Safety'},
        frPrimarySurvey: {label: 'Primary Survey (ABCs)'},
        frPatientHistory: {label: 'Patient History (SAMPLE/OPQRST)'},
        frVitalSigns: {label: 'Vital Signs'},
        frAirwayManagement: {label: 'Airway Management'},
        frBreathingSupport: {label: 'Breathing Support (O₂/BVM)'},
        frCprAed: {label: 'CPR & AED'},
        frBleedingControl: {label: 'Bleeding Control & Tourniquet'},
        frShockManagement: {label: 'Shock Recognition & Management'},
        frBurnManagement: {label: 'Burn Assessment & Management'},
        frSplinting: {label: 'Splinting & Immobilization'},
        frWoundCare: {label: 'Wound Care & Bandaging'},
        frMedicalEmergencies: {label: 'Medical Emergency Recognition'},
        frMedicationAssist: {label: 'Medication Assistance'},
        frPatientMovement: {label: 'Patient Movement & Packaging'},
        frRadioHandover: {label: 'Radio Communication & Handover'}
    },
    wildland: {
        // Flat list - WSPP-WFF1 & Structure Protection (BC)
        // Initial Attack / Suppression
        wlFireBehavior: {label: 'Fire Behavior & Weather'},
        wlLCES: {label: 'LCES (Lookouts, Comms, Escape, Safety)'},
        wl10Standard: {label: '10 Standard Fire Orders'},
        wl18Watchouts: {label: '18 Watchout Situations'},
        wlSizeUp: {label: 'Wildland Size-Up'},
        wlHandTools: {label: 'Hand Tools & Line Construction'},
        wlPumpsHose: {label: 'Portable Pumps & Hose Lays'},
        wlMopUp: {label: 'Mop-Up & Patrol'},
        // Structure Protection
        wlStructureTriage: {label: 'Structure Triage'},
        wlDefensibleSpace: {label: 'Defensible Space Assessment'},
        wlSprinklers: {label: 'Sprinkler Deployment (SPU)'},
        wlStructurePrep: {label: 'Structure Prep (vents, gutters, fuels)'},
        wlInterfaceTactics: {label: 'Interface Tactics'}
    }
};

// Helper to get all competencies for a category, merging predefined with custom
export const getAllCompetencies = (category, customCompetencies = {}) => {
    if (category === 'ofc') {
        // OFC uses the existing Competencies object
        return Competencies;
    }

    const predefined = TechnicalCompetencies[category] || {};
    const custom = customCompetencies[category] || [];

    // Convert custom array to object format
    const customObj = custom.reduce((acc, item) => {
        acc[item.key] = {
            label: item.label,
            ...(item.level && {level: item.level}),
            custom: true
        };
        return acc;
    }, {});

    return {...predefined, ...customObj};
};

// Helper to get competencies grouped by level for a category
export const getCompetenciesByLevel = (category, customCompetencies = {}) => {
    const competencies = getAllCompetencies(category, customCompetencies);
    const categoryConfig = CompetencyCategories[category];

    if (!categoryConfig?.levels) {
        // Flat list - return as single group
        return {all: competencies};
    }

    // Group by level
    const grouped = {};
    categoryConfig.levels.forEach(level => {
        grouped[level] = {};
    });

    Object.entries(competencies).forEach(([key, value]) => {
        const level = value.level || 'all';
        if (grouped[level]) {
            grouped[level][key] = value;
        }
    });

    return grouped;
};

// Helper to get competency label from any source (OFC, Technical, or custom)
// Returns the label, shortLabel, or falls back to the key itself
export const getCompetencyLabel = competencyKey => {
    if (!competencyKey || typeof competencyKey !== 'string') {
        return competencyKey || '';
    }

    // Check OFC competencies first
    if (Competencies[competencyKey]) {
        const {shortLabel, label} = Competencies[competencyKey];
        return shortLabel || label || competencyKey;
    }

    // Check technical competencies
    for (const category of Object.values(TechnicalCompetencies)) {
        if (category[competencyKey]) {
            const {shortLabel, label} = category[competencyKey];
            return shortLabel || label || competencyKey;
        }
    }

    // Fall back to the key itself (for custom competencies)
    return competencyKey;
};

// Helper to get full competency data from any source
export const getCompetencyData = competencyKey => {
    if (!competencyKey || typeof competencyKey !== 'string') {
        return null;
    }

    // Check OFC competencies first
    if (Competencies[competencyKey]) {
        return {key: competencyKey, ...Competencies[competencyKey], category: 'ofc'};
    }

    // Check technical competencies
    for (const [categoryKey, category] of Object.entries(TechnicalCompetencies)) {
        if (category[competencyKey]) {
            return {key: competencyKey, ...category[competencyKey], category: categoryKey};
        }
    }

    // Return null for custom/unknown competencies
    return null;
};
